import { useState, useEffect } from 'react';
import { versionLine, versionTitle } from '@/lib/version';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Menu, X, PenSquare, Search, GitBranch, BrainCircuit } from 'lucide-react';
import { SidebarUserMenu } from './SidebarUserMenu';
import { ChatSidebar } from '../chat/ChatSidebar';
import { UpgradeBanner } from '../billing/UpgradeBanner';
import { useChatStore } from '@/stores/chatStore';
import { useSetupStore } from '@/stores/setupStore';
import { useTraceStore } from '@/stores/traceStore';
import { ReasoningPanel } from '../debug/ReasoningPanel';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  {
    path: '/chat',
    label: 'New chat',
    icon: <PenSquare className="w-5 h-5" />,
  },
  {
    path: '/search',
    label: 'Search chats',
    icon: <Search className="w-5 h-5" />,
  },
  {
    path: '/changes',
    label: 'Changes',
    icon: <GitBranch className="w-5 h-5" />,
  },
  {
    path: '/report',
    label: 'Report',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    path: '/license',
    label: 'License',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    path: '/audit',
    label: 'Audit Log',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  {
    path: '/account',
    label: 'Account',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

type PageContext = 'report';

function getPageContext(_pathname: string): PageContext {
  return 'report';
}

export function AppLayout() {
  const navigate = useNavigate();
  const reasoningOpen = useTraceStore((s) => s.open);
  const toggleReasoning = useTraceStore((s) => s.toggle);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { conversations, loadConversations, clearCurrentConversation } = useChatStore();
  const setupConversationId = useSetupStore((s) => s.setupConversationId);
  const loadSetupState = useSetupStore((s) => s.loadSetupState);

  useEffect(() => {
    loadConversations();
    loadSetupState();
  }, [loadConversations, loadSetupState]);

  // Pin the Zoom-setup chat to the top of Recents so its star stays visible.
  const setupConv = conversations.find((c) => c.id === setupConversationId);
  const recentConversations = (
    setupConv ? [setupConv, ...conversations.filter((c) => c.id !== setupConversationId)] : conversations
  ).slice(0, 10);

  const isActive = (path: string) => {
    if (path === '/chat') return location.pathname === '/chat';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isOnChatPage = location.pathname === '/chat' || location.pathname.startsWith('/chat/');
  const isOnReportPage = location.pathname === '/report';
  const isOnLicensePage = location.pathname === '/license';
  const isOnAuditPage = location.pathname === '/audit';
  const isOnSettingsPage = location.pathname === '/settings';
  const isOnAccountPage = location.pathname === '/account';
  const isOnSearchPage = location.pathname === '/search';
  const isOnChangesPage = location.pathname === '/changes' || location.pathname.startsWith('/changes/');
  const pageContext = getPageContext(location.pathname);

  const handleNav = (path: string) => {
    if (path === '/chat') {
      clearCurrentConversation();
    }
    navigate(path);
    setSidebarOpen(false);
  };

  const handleSelectRecent = (id: string) => {
    // Navigate to the REST route; ChatPage loads the conversation from the param.
    navigate(`/chat/${id}`);
    setSidebarOpen(false);
  };

  return (
    <div className="h-screen overflow-hidden bg-background text-foreground flex">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-56 bg-card border-r border-border flex flex-col transform transition-transform duration-200 ease-in-out lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => handleNav('/')}>
            <video
              src="/zac-avatar.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-8 h-8 rounded-lg object-cover"
            />
            <span className="text-lg font-semibold">Ask <span className="text-primary">Zac</span></span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="py-4 px-2 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-secondary text-foreground'
                  : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
          {/* ZAC's reasoning: a panel over any page, informative only (R4). */}
          <button
            onClick={toggleReasoning}
            aria-pressed={reasoningOpen}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              reasoningOpen ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
            }`}
          >
            <BrainCircuit className="w-5 h-5" />
            <span>Reasoning</span>
          </button>
        </nav>

        {/* Recent conversations */}
        {conversations.length > 0 && (
          <div className="flex-1 overflow-y-auto px-2 pb-2">
            <h3 className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">Recents</h3>
            <ul className="space-y-0.5">
              {recentConversations.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => handleSelectRecent(c.id)}
                    className="w-full truncate rounded-lg px-3 py-1.5 text-left text-sm text-muted-foreground hover:bg-secondary/50 hover:text-foreground transition-colors"
                  >
                    {c.id === setupConversationId && <span className="mr-1">⭐</span>}
                    {c.id === setupConversationId ? 'Zoom setup' : (c.title ?? 'New conversation')}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer with User Menu, and the version as WoW shows its patch (docs/versioning.md) */}
        <div className="p-2 border-t border-border">
          <SidebarUserMenu />
          <p className="mt-1 truncate px-3 text-[10px] tabular-nums text-muted-foreground/60" title={versionTitle()}>{versionLine()}</p>
        </div>
      </aside>

      {/* Main Content */}
      {/* With the reasoning panel open, wide screens make room for it instead of covering the page. */}
      <div className={`flex-1 flex flex-col min-w-0 ${reasoningOpen ? 'lg:mr-96' : ''}`}>
        {/* Mobile top bar with hamburger */}
        <div className="h-14 flex items-center px-4 border-b border-border lg:hidden shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 ml-3 cursor-pointer" onClick={() => navigate('/')}>
            <video
              src="/zac-avatar.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-6 h-6 rounded object-cover"
            />
            <span className="text-sm font-semibold">Ask <span className="text-primary">Zac</span></span>
          </div>
        </div>
        <UpgradeBanner />
        {/* Route outlet: viewport-bounded. The chat fills it exactly (h-full)
            and scrolls its OWN message list; taller non-chat pages (Settings,
            Account) scroll within this region instead of growing the page. */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Outlet />
        </div>
      </div>

      {/* Chat Sidebar - hidden on /chat, /search, /changes (its own prompt box), /report (which has its own sidebar), /license, /audit, and /settings */}
      {!isOnChatPage && !isOnSearchPage && !isOnChangesPage && !isOnReportPage && !isOnLicensePage && !isOnAuditPage && !isOnSettingsPage && !isOnAccountPage && <ChatSidebar context={pageContext} />}
      <ReasoningPanel />
    </div>
  );
}
